import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import yaml from 'js-yaml';
import { useAuth } from '../../contexts/AuthContext';
import { usePermission, PERMISSIONS } from '../../hooks/usePermission';

// ─── Feature flags ─────────────────────────────────────────────────────────
//
// LEARNERS_CAN_VIEW_PAST_PAPERS — set to `true` to expose the "Past paper
// coverage" block (verbatim past-paper questions) to learners. When `false`,
// only teachers, school admins, and admins see it.
//
// Set to `false` on 2026-04-28 — a 2026 P1 paper had been ingested before
// learners sat the test, leaking verbatim questions (incl. PawCare scenario)
// to Gr10 students via /curriculum-reference. Flip back to `true` once the
// affected tests have been administered (target: Mon 2026-05-04) — or keep
// at `false` permanently and treat verbatim past-paper exposure as a
// teacher-only feature.
const LEARNERS_CAN_VIEW_PAST_PAPERS = false;

// ─── Types ─────────────────────────────────────────────────────────────────

type PageStatus = 'draft' | 'needs_review' | 'vetted';
type Modality = 'theory' | 'practical' | 'both';

interface PageIndexEntry {
  subtopic_code: string;
  grade: number;
  topic_num: number;
  topic_title: string;
  subtopic_num: number;
  skill: string;
  modality: Modality;
  status: PageStatus;
  vetted_by: string | null;
  vetted_at: string | null;
  exemplar_count: number;
  harvested_from: string | null;
  file_path: string;
}

interface LoadedPage {
  frontmatter: PageIndexEntry;
  body: string;
}

interface PastPaperQuestion {
  year: number;
  paper: number;
  q_num: string;
  marks: number;
  taxonomy_level: 'L1' | 'L2' | 'L3';
  text: string;
  subtopic_codes: string[];
}

interface PastPaperStats {
  count: number;
  marks_min: number;
  marks_max: number;
  taxonomy: { L1: number; L2: number; L3: number };
  questions: PastPaperQuestion[];
}

// ─── Helpers ───────────────────────────────────────────────────────────────

const MODALITY_LABEL: Record<Modality, string> = {
  theory: 'T',
  practical: 'P',
  both: 'T/P',
};

const MODALITY_COLOR: Record<
  Modality,
  'default' | 'primary' | 'secondary' | 'success' | 'info'
> = {
  theory: 'info',
  practical: 'secondary',
  both: 'primary',
};

const STATUS_COLOR: Record<
  PageStatus,
  'default' | 'warning' | 'success'
> = {
  draft: 'default',
  needs_review: 'warning',
  vetted: 'success',
};

function splitFrontmatter(raw: string): { frontmatter: string; body: string } {
  const match = raw.match(/^---\n([\s\S]*?)\n---\n?([\s\S]*)$/);
  if (!match) {
    return { frontmatter: '', body: raw };
  }
  return { frontmatter: match[1], body: match[2] };
}

// ─── Main page ─────────────────────────────────────────────────────────────

const CurriculumReferencePage: React.FC = () => {
  const { user, impersonatedLearnerGrade } = useAuth();
  // RBAC Option B gates (see docs/RBAC_DESIGN.md §3). The previous
  // canViewAll = isTeacher || isAdmin || isSchoolAdmin derivation has been
  // split into the two distinct capabilities it was actually granting:
  // showing pages outside the user's enrolled grade, and showing pages
  // that aren't yet vetted.
  const canViewAllGrades = usePermission(PERMISSIONS.VIEW_ALL_GRADES);
  const canViewDrafts = usePermission(PERMISSIONS.VIEW_DRAFT_WIKI_PAGES);
  const canViewPastPapers = usePermission(PERMISSIONS.VIEW_PAST_PAPER_QUESTIONS);
  // Students are filtered to their enrolled grade; teachers/admins see all grades.
  // When impersonating, use the impersonated learner's grade, not the teacher's profile.
  const studentGrade: number | null = canViewAllGrades
    ? null
    : (impersonatedLearnerGrade ?? user?.user_profile?.learner_profile?.grade_level ?? null);

  const [pages, setPages] = useState<PageIndexEntry[] | null>(null);
  const [indexError, setIndexError] = useState<string | null>(null);
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [loadedPage, setLoadedPage] = useState<LoadedPage | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [pastPapers, setPastPapers] = useState<Record<string, PastPaperStats> | null>(null);

  // Load the past-paper aggregate on mount (tolerate 404 → {}).
  useEffect(() => {
    let cancelled = false;
    fetch('/wiki/it/past_papers_by_subtopic.json')
      .then((res) => (res.ok ? res.json() : {}))
      .then((data) => {
        if (!cancelled) setPastPapers(data || {});
      })
      .catch(() => {
        if (!cancelled) setPastPapers({});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load the per-subject index on mount.
  useEffect(() => {
    let cancelled = false;
    fetch('/wiki/it/index.json')
      .then((res) => {
        if (!res.ok) throw new Error(`index fetch failed: ${res.status}`);
        return res.json();
      })
      .then((data: { pages: PageIndexEntry[] }) => {
        if (!cancelled) {
          setPages(data.pages);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setIndexError(err.message || 'unable to load wiki index');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load the selected page's markdown.
  useEffect(() => {
    if (!selectedCode || !pages) {
      setLoadedPage(null);
      return;
    }
    const entry = pages.find((p) => p.subtopic_code === selectedCode);
    if (!entry) {
      setLoadedPage(null);
      return;
    }
    let cancelled = false;
    setPageLoading(true);
    setPageError(null);
    fetch(`/wiki/it/${entry.file_path}`)
      .then((res) => {
        if (!res.ok) throw new Error(`page fetch failed: ${res.status}`);
        return res.text();
      })
      .then((text) => {
        if (cancelled) return;
        const { frontmatter: fmRaw, body } = splitFrontmatter(text);
        let fm: PageIndexEntry = entry;
        if (fmRaw) {
          try {
            fm = { ...entry, ...(yaml.load(fmRaw) as Partial<PageIndexEntry>) };
          } catch {
            // fall back to index entry
          }
        }
        setLoadedPage({ frontmatter: fm, body });
      })
      .catch((err) => {
        if (!cancelled) {
          setPageError(err.message || 'unable to load page');
        }
      })
      .finally(() => {
        if (!cancelled) setPageLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedCode, pages]);

  // Pages the current user is allowed to see. Teachers see everything;
  // learners see all pages for their own grade (draft or vetted).
  // If grade is unknown, fall back to vetted-only across all grades.
  const visiblePages = useMemo(() => {
    if (!pages) return null;
    if (canViewDrafts) return pages;
    if (studentGrade !== null) return pages.filter((p) => p.grade === studentGrade);
    return pages.filter((p) => p.status === 'vetted');
  }, [pages, canViewDrafts, studentGrade]);

  // Group pages by topic and grade for the sidebar.
  const grouped = useMemo(() => {
    if (!visiblePages) return [];
    type TopicGroup = {
      topic_num: number;
      topic_title: string;
      grades: Record<number, PageIndexEntry[]>;
    };
    const byTopic = new Map<number, TopicGroup>();
    for (const p of visiblePages) {
      let t = byTopic.get(p.topic_num);
      if (!t) {
        t = { topic_num: p.topic_num, topic_title: p.topic_title, grades: {} };
        byTopic.set(p.topic_num, t);
      }
      if (!t.grades[p.grade]) t.grades[p.grade] = [];
      t.grades[p.grade].push(p);
    }
    const sorted = Array.from(byTopic.values()).sort(
      (a, b) => a.topic_num - b.topic_num,
    );
    for (const t of sorted) {
      for (const g of Object.keys(t.grades).map(Number)) {
        t.grades[g].sort((a, b) => a.subtopic_num - b.subtopic_num);
      }
    }
    return sorted;
  }, [visiblePages]);

  if (indexError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Could not load wiki index: {indexError}</Alert>
      </Box>
    );
  }

  if (!pages) {
    return (
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={24} />
        <Typography>Loading curriculum reference…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* Sidebar */}
      <Paper
        elevation={1}
        sx={{
          width: 360,
          minWidth: 360,
          overflowY: 'auto',
          borderRight: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Curriculum Reference
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Information Technology · IEB SAG Appendix G
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
            <Chip label={`${visiblePages?.length ?? 0} pages`} size="small" />
            {canViewDrafts && (
              <>
                <Chip
                  label={`${pages.filter((p) => p.status === 'vetted').length} vetted`}
                  size="small"
                  color="success"
                />
                <Chip
                  label={`${pages.filter((p) => p.status === 'needs_review').length} review`}
                  size="small"
                  color="warning"
                />
                <Chip
                  label={`${pages.filter((p) => p.status === 'draft').length} draft`}
                  size="small"
                />
              </>
            )}
          </Stack>
        </Box>
        <Divider />
        {grouped.map((topic) => (
          <Box key={topic.topic_num}>
            <Box sx={{ px: 2, pt: 2, pb: 0.5 }}>
              <Typography variant="subtitle2" color="primary">
                Topic {topic.topic_num}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {topic.topic_title}
              </Typography>
            </Box>
            {Object.keys(topic.grades)
              .map(Number)
              .sort((a, b) => a - b)
              .map((grade) => (
                <Box key={grade} sx={{ pb: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{ px: 2, pt: 1, display: 'block', fontWeight: 600 }}
                  >
                    Grade {grade}
                  </Typography>
                  <List dense disablePadding>
                    {topic.grades[grade].map((page) => {
                      const isSelected = page.subtopic_code === selectedCode;
                      return (
                        <ListItemButton
                          key={page.subtopic_code}
                          selected={isSelected}
                          onClick={() => setSelectedCode(page.subtopic_code)}
                          sx={{ px: 2 }}
                        >
                          <ListItemText
                            primary={
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography
                                  component="span"
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontSize: 13,
                                    fontWeight: 500,
                                  }}
                                >
                                  {page.subtopic_code}
                                </Typography>
                                <Chip
                                  label={MODALITY_LABEL[page.modality]}
                                  size="small"
                                  color={MODALITY_COLOR[page.modality]}
                                  sx={{ height: 18, fontSize: 10 }}
                                />
                                {page.status !== 'vetted' && (
                                  <Chip
                                    label={page.status === 'needs_review' ? 'review' : 'draft'}
                                    size="small"
                                    color={STATUS_COLOR[page.status]}
                                    variant="outlined"
                                    sx={{ height: 18, fontSize: 10 }}
                                  />
                                )}
                              </Stack>
                            }
                            secondary={page.skill}
                            secondaryTypographyProps={{
                              sx: {
                                fontSize: 12,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                              },
                            }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Box>
              ))}
            <Divider />
          </Box>
        ))}
      </Paper>

      {/* Main pane */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 4 }}>
        {!selectedCode && (
          <Box sx={{ maxWidth: 600, mx: 'auto', mt: 8, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
              Curriculum Reference
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Pick a subtopic from the sidebar to read its full content —
              skill, content scope, overview, key terms, worked examples,
              exemplar questions, and common misconceptions.
            </Typography>
            <Typography color="text.secondary" variant="body2">
              IEB Information Technology · Appendix G · Grade 10
            </Typography>
          </Box>
        )}

        {selectedCode && pageLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} />
            <Typography>Loading {selectedCode}…</Typography>
          </Box>
        )}

        {selectedCode && pageError && (
          <Alert severity="error">Could not load {selectedCode}: {pageError}</Alert>
        )}

        {selectedCode && loadedPage && !pageLoading && (
          <Box sx={{ maxWidth: 900 }}>
            <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
              <Chip
                label={`Status: ${loadedPage.frontmatter.status}`}
                size="small"
                color={STATUS_COLOR[loadedPage.frontmatter.status]}
              />
              <Chip
                label={`Modality: ${loadedPage.frontmatter.modality}`}
                size="small"
                color={MODALITY_COLOR[loadedPage.frontmatter.modality]}
              />
              <Chip
                label={`Grade ${loadedPage.frontmatter.grade}`}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`Topic ${loadedPage.frontmatter.topic_num} · ${loadedPage.frontmatter.topic_title}`}
                size="small"
                variant="outlined"
              />
              {loadedPage.frontmatter.vetted_at && (
                <Chip
                  label={`Vetted ${loadedPage.frontmatter.vetted_at} by ${loadedPage.frontmatter.vetted_by ?? '?'}`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>
            <Box
              className="wiki-markdown"
              sx={{
                '& h1': { fontSize: 28, mt: 2, mb: 1 },
                '& h2': { fontSize: 22, mt: 3, mb: 1, borderBottom: '1px solid', borderColor: 'divider', pb: 0.5 },
                '& h3': { fontSize: 18, mt: 2, mb: 0.5 },
                '& h4': { fontSize: 16, mt: 1.5, mb: 0.5 },
                '& p': { lineHeight: 1.6, mb: 1.5 },
                '& ul, & ol': { pl: 3, mb: 1.5 },
                '& li': { lineHeight: 1.6 },
                '& table': { borderCollapse: 'collapse', my: 2, width: '100%' },
                '& th, & td': { border: '1px solid', borderColor: 'divider', px: 1.5, py: 1, textAlign: 'left' },
                '& th': { backgroundColor: 'action.hover', fontWeight: 600 },
                '& code': { backgroundColor: 'action.hover', px: 0.75, py: 0.25, borderRadius: 0.5, fontFamily: 'monospace', fontSize: 13 },
                '& pre': { backgroundColor: 'action.hover', p: 2, borderRadius: 1, overflowX: 'auto', fontSize: 13 },
                '& pre code': { backgroundColor: 'transparent', p: 0 },
                '& blockquote': { borderLeft: '4px solid', borderColor: 'primary.light', pl: 2, ml: 0, color: 'text.secondary', my: 1.5 },
                '& details': { border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 2, py: 1, my: 1.5 },
                '& summary': { cursor: 'pointer', fontWeight: 500 },
                '& hr': { border: 0, borderTop: '1px solid', borderColor: 'divider', my: 3 },
              }}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  // Translate intra-wiki "10.1.2.md" links into in-page navigation
                  // (set selectedCode) instead of letting the browser hit a 404.
                  // Cross-grade paths like "../gr11/11.1.2.md" also work — we just
                  // pull the trailing subtopic code.
                  a: ({ href, children, ...rest }) => {
                    const codeMatch = href?.match(/(?:^|\/)(\d+\.\d+\.\d+)(?:\.md)?(?:#.*)?$/);
                    const code = codeMatch?.[1];
                    const known = code && pages?.some((p) => p.subtopic_code === code);
                    if (known) {
                      return (
                        <a
                          href={`#${code}`}
                          onClick={(e) => {
                            e.preventDefault();
                            setSelectedCode(code);
                          }}
                          {...rest}
                        >
                          {children}
                        </a>
                      );
                    }
                    const isExternal = !!href && /^https?:\/\//i.test(href);
                    return (
                      <a
                        href={href}
                        target={isExternal ? '_blank' : undefined}
                        rel={isExternal ? 'noopener noreferrer' : undefined}
                        {...rest}
                      >
                        {children}
                      </a>
                    );
                  },
                }}
              >
                {loadedPage.body}
              </ReactMarkdown>
            </Box>

            {/* Past paper coverage — gated by RBAC permission VIEW_PAST_PAPER_QUESTIONS
                (currently teachers + school_admins + admins; see utils/permissions.ts).
                LEARNERS_CAN_VIEW_PAST_PAPERS at the top of this file is a feature
                flag that opens the block to learners regardless of permission. */}
            {(canViewPastPapers || LEARNERS_CAN_VIEW_PAST_PAPERS) && pastPapers && pastPapers[loadedPage.frontmatter.subtopic_code] && (() => {
              const stats = pastPapers[loadedPage.frontmatter.subtopic_code];
              if (!stats || stats.count === 0) return null;
              return (
                <Paper
                  variant="outlined"
                  sx={{ mt: 4, p: 2.5 }}
                >
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Past paper coverage
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 1 }}>
                    <Chip
                      label={`Examined ${stats.count} time${stats.count === 1 ? '' : 's'}`}
                      size="small"
                      color="primary"
                    />
                    <Chip
                      label={`Marks ${stats.marks_min === stats.marks_max ? stats.marks_min : `${stats.marks_min}–${stats.marks_max}`}`}
                      size="small"
                      variant="outlined"
                    />
                    {(['L1', 'L2', 'L3'] as const).map((lvl) =>
                      stats.taxonomy[lvl] > 0 ? (
                        <Chip
                          key={lvl}
                          label={`${lvl} × ${stats.taxonomy[lvl]}`}
                          size="small"
                          variant="outlined"
                        />
                      ) : null,
                    )}
                  </Stack>

                  <details style={{ marginTop: 8 }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 500 }}>
                      Show verbatim past-paper questions ({stats.count})
                    </summary>
                    <List dense disablePadding>
                      {stats.questions.map((q, idx) => (
                        <Box
                          key={`${q.year}-p${q.paper}-${q.q_num}-${idx}`}
                          sx={{
                            py: 1,
                            borderTop: idx === 0 ? 0 : '1px dashed',
                            borderColor: 'divider',
                          }}
                        >
                          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 0.5 }}>
                            <Chip
                              label={`${q.year} P${q.paper} Q${q.q_num}`}
                              size="small"
                              sx={{ fontFamily: 'monospace', fontSize: 11, height: 20 }}
                            />
                            <Chip
                              label={`${q.marks}m`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: 11, height: 20 }}
                            />
                            <Chip
                              label={q.taxonomy_level}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: 11, height: 20 }}
                            />
                          </Stack>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {q.text}
                          </Typography>
                        </Box>
                      ))}
                    </List>
                  </details>
                </Paper>
              );
            })()}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CurriculumReferencePage;
